import { AdminResponse } from "entities/auth/api/getAdmins";
import useCreateAdminMutation from "entities/auth/mutations/useCreateAdminMutation";
import useUpdateAdminMutation from "entities/auth/mutations/useUpdateAdminMutation";
import useAdminListQuery from "entities/auth/queries/useAdminListQuery";
import { AdminRole } from "entities/auth/types";
import { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import useInputState from "shared/hooks/useInputState";
import { createContext } from "shared/utils/context";
import emailValidator from "shared/validators/emailValidator";
import { useRolesAndPermissionsPage } from "../../../context";
import { AdminDetails } from "./types";

const [Provider, useAdminDetails] = createContext<AdminDetails>({
  name: "AdminDetailsContext",
  hookName: "useAdminDetails",
  providerName: "AdminDetailsProvider",
});

const AdminDetailsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { adminDetailsDrawer } = useRolesAndPermissionsPage();
  const { data } = useAdminListQuery();
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const emailInput = useInputState("adminEmail", "", emailValidator);

  const handleCloseDrawer = () => {
    adminDetailsDrawer.closeDrawer();
    emailInput.setValue("");
    emailInput.setErrors([]);
    setRoles([]);
  };

  const { mutate: createAdmin, isPending: isPendingCreateAdmin } =
    useCreateAdminMutation(handleCloseDrawer);

  const { mutate: updateAdmin, isPending: isPendingUpdateAdmin } =
    useUpdateAdminMutation(handleCloseDrawer);

  const selectedAdmin = useMemo<AdminResponse | null>(() => {
    return data?.find((u) => u.id === adminDetailsDrawer.drawerDetails) ?? null;
  }, [adminDetailsDrawer.drawerDetails, data]);

  useEffect(() => {
    if (selectedAdmin) {
      emailInput.setValue(selectedAdmin.email);
      setRoles(selectedAdmin.roles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdmin]);

  const handleSaveAdmin = () => {
    if (!roles) {
      return;
    }

    if (adminDetailsDrawer.drawerDetails) {
      updateAdmin({
        id: adminDetailsDrawer.drawerDetails,
        roles,
      });
    } else {
      createAdmin({
        email: emailInput.input.value,
        roles,
      });
    }
  };

  const isLoading = !selectedAdmin && !!adminDetailsDrawer.drawerDetails;
  const isDisabled = !emailInput.input.value || !!emailInput.input.errors.length || !roles.length;
  const isPending = isPendingCreateAdmin || isPendingUpdateAdmin;

  const value: AdminDetails = {
    isPending,
    isDisabled,
    isLoading,
    selectedAdmin,
    emailInput,
    roles,
    setRoles,
    handleCloseDrawer,
    handleSaveAdmin,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { AdminDetailsProvider, useAdminDetails };
